// src/services/toolRouter.js
// UPDATED: API paths aligned exactly with api.js endpoint definitions.
// Uses previu_token (same key as api.js axios interceptor).

import {
  applyPatientToSlotSearch, applyPatientToOrder, pickAddress, compactAddressOptions,
  sameResolvedPincode, buildOrderSummary, missingOrderFields,
} from '../utils/labOrderIdentity';

const BASE_URL = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

function authHeaders(jwt) {
  return {
    'Content-Type': 'application/json',
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };
}

function authGetHeaders(jwt) {
  // GET requests must NOT include Content-Type — it triggers a CORS preflight
  // which the server may reject. Only Authorization header is needed.
  return {
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };
}

async function apiGet(path, jwt) {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: authGetHeaders(jwt),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = body?.message ?? `GET ${path} → ${res.status}`;
    console.error('[toolRouter] apiGet failed:', url, res.status, body);
    throw new Error(msg);
  }
  return res.json();
}

async function apiPost(path, body, jwt) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: authHeaders(jwt),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message ?? `POST ${path} → ${res.status}`);
  }
  return res.json();
}

async function apiPut(path, body, jwt) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'PUT',
    headers: authHeaders(jwt),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message ?? `PUT ${path} → ${res.status}`);
  }
  return res.json();
}

// ─── book_appointment ────────────────────────────────────────────────────────
// Matches api.js appointmentAPI shape exactly:
//   GET  /appointments/patient/{patientId}
//   GET  /appointments/patient/{patientId}/upcoming
//   GET  /appointments/available-slots?doctorId=&date=
//   GET  /doctors
//   POST /appointments

async function handleBookAppointment(input, jwt) {
  const { action, patientId, doctorId, appointmentDate, appointmentTime, appointmentType, confirmed } = input;

  switch (action) {
    case 'list_doctors':
      // Returns all doctors; isAvailable flag on each lets Claude filter
      return apiGet('/doctors', jwt);

    case 'get_appointments':
      // Upcoming appointments for this patient
      return apiGet(`/appointments/patient/${patientId}/upcoming`, jwt);

    case 'get_available_slots': {
      // GET /appointments/available-slots?doctorId=&date=
      if (!doctorId || !appointmentDate) {
        return { error: 'doctorId and appointmentDate (YYYY-MM-DD) are required.' };
      }
      // Guard: doctorId must be a valid UUID — Claude must call list_doctors first
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(doctorId)) {
        return {
          error: `Invalid doctorId "${doctorId}". Call list_doctors first and use the UUID "id" field from the response. Never guess or invent a doctor ID.`,
        };
      }
      return apiGet(`/appointments/available-slots?doctorId=${doctorId}&date=${appointmentDate}`, jwt);
    }

    case 'create_appointment': {
      if (!confirmed) {
        return {
          status: 'awaiting_confirmation',
          message: 'Please confirm with the patient before booking.',
        };
      }
      if (!doctorId || !appointmentDate || !appointmentTime) {
        return { error: 'doctorId, appointmentDate, and appointmentTime are required.' };
      }
      // Guard: doctorId must be a valid UUID
      const uuidRegex2 = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex2.test(doctorId)) {
        return {
          error: `Invalid doctorId "${doctorId}". Call list_doctors first and use the UUID "id" field from the response.`,
        };
      }

      const appointmentPayload = {
        patientId,
        doctorId,
        appointmentDate,
        appointmentTime,
        appointmentType: appointmentType ?? 'CONSULTATION',
      };

      // 16 Jul 2026: no longer calls /appointments directly — doctor
      // appointments now gate on a verified Razorpay payment first, same
      // "tool result → payment card, defer the real action to a click"
      // pattern already used for lab-test orders. Fetches patient + doctor
      // display info (neither is carried in the create_appointment tool
      // input itself, unlike lab orders where orderRequest.patients[0]
      // already had this) so the card can show a real doctor name/patient
      // context rather than IDs.
      let patient = null, doctor = null;
      try {
        [patient, doctor] = await Promise.all([
          apiGet(`/patients/${patientId}`, jwt),
          apiGet(`/doctors/${doctorId}`, jwt),
        ]);
      } catch (e) {
        console.warn('[toolRouter] create_appointment: patient/doctor lookup for card display failed', e.message);
      }

      return {
        type: 'appointment_payment_card',
        patient: {
          id: patientId,
          firstName: patient?.firstName || '',
          lastName:  patient?.lastName || '',
          email: patient?.email,
          phone: patient?.phone,
        },
        doctorId,
        doctorName: doctor ? `Dr. ${doctor.firstName || ''} ${doctor.lastName || ''}`.trim() : undefined,
        appointmentType: appointmentPayload.appointmentType,
        appointmentPayload,
      };
    }

    default:
      return { error: `Unknown action: ${action}` };
  }
}

// ─── book_navigator_appointment ────────────────────────────────────────────
// Matches api.js followUpAPI shape exactly — the same "Confirm your
// follow-up" backend PatientAppointmentsPage.jsx's ConfirmFollowUpCard
// already uses, now reachable from chat too:
//   GET  /patients/{patientId}/follow-up/available-slots?date=
//   POST /patients/{patientId}/follow-up/confirm
//
// Deliberately separate from handleBookAppointment above — there is exactly
// one provider (the patient's own navigator), and confirm here books
// immediately: no payment step, so no payment-card result type like
// create_appointment's appointment_payment_card.
async function handleBookNavigatorAppointment(input, jwt) {
  const { action, patientId, appointmentDate, appointmentTime, summary, patientNotes, confirmed } = input;

  switch (action) {
    case 'get_available_slots': {
      if (!appointmentDate) {
        return { error: 'appointmentDate (YYYY-MM-DD) is required.' };
      }
      return apiGet(`/patients/${patientId}/follow-up/available-slots?date=${appointmentDate}`, jwt);
    }

    case 'confirm': {
      if (!confirmed) {
        return {
          status: 'awaiting_confirmation',
          message: 'Please confirm the date and time with the patient before booking.',
        };
      }
      if (!appointmentDate || !appointmentTime) {
        return { error: 'appointmentDate and appointmentTime are required.' };
      }
      return apiPost(`/patients/${patientId}/follow-up/confirm`, {
        date: appointmentDate,
        time: appointmentTime,
        summary,
        patientNotes,
      }, jwt);
    }

    default:
      return { error: `Unknown action: ${action}` };
  }
}

// ─── add_navigator_note ─────────────────────────────────────────────────────
// POST /patients/{patientId}/navigator-notes — PatientNavigatorNoteController.
// Single-action tool, no `action` field to switch on (see toolRouter's own
// dispatcher below). Deliberately NOT patientAPI.update's `notes` field
// (PatientDetailPage's Navigator Notes textarea, a single overwritable
// column) — see PatientNavigatorNoteService's backend javadoc for why a
// chat-triggered write there would silently clobber whatever the Navigator
// had already typed.
async function handleAddNavigatorNote(input, jwt) {
  const { patientId, note, confirmed } = input;

  if (!confirmed) {
    return {
      status: 'awaiting_confirmation',
      message: 'Please read the note back to the patient and confirm it before sending.',
    };
  }
  if (!note || !String(note).trim()) {
    return { error: 'note is required.' };
  }
  return apiPost(`/patients/${patientId}/navigator-notes`, { content: note }, jwt);
}

// ─── order_lab_test ──────────────────────────────────────────────────────────
// Matches api.js labTestAPI shape exactly:
//   GET  /labtest/catalog/list?minPrice=&maxPrice=&gender=&page=&pageSize=
//   POST /labtest/slots/search
//   POST /labtest/orders

async function handleOrderLabTest(input, jwt) {
  const { action, patientId, minPrice, maxPrice, gender, page, pageSize, slotSearchRequest, orderRequest, confirmed, addressId } = input;

  switch (action) {
    case 'list_catalog': {
      const params = new URLSearchParams({
        minPrice:  minPrice  ?? 0,
        maxPrice:  maxPrice  ?? 10000,
        gender:    gender    ?? 'MALE',
        page:      page      ?? 1,
        pageSize:  pageSize  ?? 5,
      });
      return apiGet(`/labtest/catalog/list?${params}`, jwt);
    }

    case 'search_slots': {
      if (!slotSearchRequest) return { error: 'slotSearchRequest body is required.' };

      // The patient's name/age/gender come from their profile, not from what the model typed: the model
      // no longer needs (or is given) the profile to order. Falls back to the model's values only if the
      // profile cannot be fetched, so slot search still works.
      let slotRequest = slotSearchRequest;
      try {
        slotRequest = applyPatientToSlotSearch(slotSearchRequest, await apiGet(`/patients/${patientId}`, jwt));
      } catch (e) {
        console.warn('[toolRouter] search_slots: profile lookup failed, using the request as sent', e.message);
      }

      // Guard: Thyrocare requires patients[] array — build one if Claude omitted it
      const safeSlotRequest = buildSafeSlotRequest(slotRequest, input);

      // POST /labtest/slots/search
      return apiPost('/labtest/slots/search', safeSlotRequest, jwt);
    }

    case 'create_order': {
      if (!confirmed) {
        return {
          status: 'awaiting_confirmation',
          message: 'Please confirm with the patient before placing the order.',
        };
      }
      if (!orderRequest) return { error: 'orderRequest body is required.' };

      // The model chooses WHAT is ordered (test items, slot) and WHICH saved address (by addressId). The
      // patient's name, phone, email, age, gender and the address text come from their own profile,
      // fetched here with their login — the model never receives them and anything it sent is overwritten.
      let profile;
      let savedAddresses;
      try {
        const [prof, addrs] = await Promise.all([
          apiGet(`/patients/${patientId}`, jwt),
          apiGet(`/patients/${patientId}/addresses`, jwt),
        ]);
        profile = prof;
        savedAddresses = Array.isArray(addrs) ? addrs : (addrs?.data ?? []);
      } catch (e) {
        console.warn('[toolRouter] create_order: profile/address lookup failed', e.message);
        return { error: "Could not load the patient's details to prepare the order. Please try again in a moment." };
      }
      const missingFields = missingOrderFields(profile);
      if (missingFields.length > 0) {
        return {
          error: `The patient's profile is missing: ${missingFields.join(', ')}. Ask the patient for it, `
            + 'save it with update_profile (action=update_profile, confirmed=true), then call create_order again.',
        };
      }
      const searchedPin = input.slotSearchRequest?.pincode ?? orderRequest.address?.pincode;
      const chosenAddress = pickAddress(savedAddresses, {
        addressId, searchedPincode: searchedPin, hasOwnAddress: !!orderRequest.address,
      });
      if (addressId && !chosenAddress) {
        return { error: "addressId does not match one of the patient's saved addresses. "
          + 'Call update_profile with action=list_address_options to see the choices.' };
      }

      // Sanitise price fields and build all required Thyrocare fields
      const completeOrder = buildCompleteOrderRequest(
        applyPatientToOrder(orderRequest, profile, chosenAddress), input);

      // 15 Jul 2026: no longer calls Thyrocare directly — chat lab-test
      // orders now gate on a verified Razorpay payment first (same
      // requirement as PatientOrderLabTestPage.jsx's self-service flow,
      // which had the identical gap: paymentDetails.payType hardcoded to
      // POSTPAID with no actual payment step anywhere). Returns a card
      // result instead; the chat UI renders LabTestPaymentCard.jsx, and
      // POST /labtest/orders only fires from THAT card's click handler
      // (via src/utils/labTestPayment.js), after payment succeeds — same
      // "tool result → widget, defer the real action to a click" pattern
      // already used for download_triage_report.
      const firstPatient = completeOrder.patients?.[0];
      const firstItem    = firstPatient?.items?.[0];

      // 15 Jul 2026 (fix): firstItem.name here is whatever Claude supplied
      // in its create_order tool call, which traces back to catalog/search
      // results earlier in the conversation — those can still be raw
      // Thyrocare SKU names ("AAROGYAM 1.2"), not the Previu-branded name,
      // depending on which prior tool call Claude drew the name from.
      // Resolve the correct Previu display name properly via the same
      // panel-pricing endpoint already used everywhere else (matching by
      // skuId against this item's raw code) — falls back to the raw name
      // only if no match is found, rather than failing the whole card.
      let panelName = firstItem?.name;
      try {
        // 16 Sep 2026: ONCOVERYX retired, replaced by TUMOUR_MALE/
        // TUMOUR_FEMALE. Both are requested here regardless of the
        // patient's gender — this lookup only matches the just-created
        // order's skuId to resolve a display name, so requesting the panel
        // the patient didn't order is harmless (it just won't match).
        // T3 is deliberately not added — it is never orderable through this
        // flow in the first place.
        const pricing = await apiPost('/labtest/panel-pricing', {
          patientId, panels: ['T1', 'T2', 'TUMOUR_MALE', 'TUMOUR_FEMALE'],
        }, jwt);
        const matched = pricing?.panels?.find(p =>
          (p.skuId || '').trim().toUpperCase() === (firstItem?.id || '').trim().toUpperCase());
        if (matched?.name) panelName = matched.name;
      } catch (e) {
        console.warn('[toolRouter] create_order: panel-pricing lookup for display name failed, ' +
          'falling back to raw name', e.message);
      }

      return {
        type: 'lab_test_payment_card',
        patient: {
          id: patientId,
          firstName: profile?.firstName || '',
          lastName:  profile?.lastName || '',
          email: profile?.email,
          phone: profile?.phone,
        },
        panelName,
        testCode:  firstItem?.id,
        orderPayload: completeOrder,
        // The review the patient confirms before paying. Drawn by the card from the patient's own profile —
        // this result goes to the widget only; Claude sees the short status built in chatService.js.
        summary: buildOrderSummary(completeOrder, profile),
        // Saved addresses the patient may switch to on the card: only those at the booked pincode, because
        // Thyrocare holds slot availability per pincode.
        savedAddresses: sameResolvedPincode(savedAddresses, completeOrder.address?.pincode),
        selectedAddressId: chosenAddress?.id ?? null,
      };
    }

    case 'get_package_details': {
      // GET /api/labtest/catalog/package/{query}
      // Returns full SKU with testsIncluded array — all individual tests in the package.
      // query can be SKU ID (e.g. 'AAROGYAM11') or package name (e.g. 'Aarogyam 1.1').
      const packageQuery = input.packageQuery || input.skuId || input.name;
      if (!packageQuery) return { error: 'packageQuery is required (SKU ID or package name).' };
      return apiGet(`/labtest/catalog/package/${encodeURIComponent(packageQuery)}`, jwt);
    }

    default:
      return { error: `Unknown action: ${action}` };
  }
}

// ─── update_profile ──────────────────────────────────────────────────────────
// Matches api.js patientAPI + addressAPI shape exactly:
//   GET /patients/{id}
//   PUT /patients/{id}
//   PUT /patients/{patientId}/addresses/{addressId}

async function handleUpdateProfile(input, jwt) {
  const { action, patientId, updates, addressUpdates, addressId, confirmed } = input;

  switch (action) {
    case 'get_profile':
      // GET /patients/{id} — matches patientAPI.get(id)
      return apiGet(`/patients/${patientId}`, jwt);

    case 'get_addresses':
      // GET /patients/{patientId}/addresses — returns saved addresses including default
      // AddressDTO.Response fields: id, addressType, street, city, state, zipCode,
      //   country, landmark, isDefault
      // Use the isDefault=true address for home collection.
      return apiGet(`/patients/${patientId}/addresses`, jwt);

    case 'list_address_options': {
      // For lab ordering: just enough to choose a saved address (id, type, city, pincode, default) —
      // never the street or landmark, which the app fills in from the profile when the order is built.
      const addrs = await apiGet(`/patients/${patientId}/addresses`, jwt);
      return { addresses: compactAddressOptions(Array.isArray(addrs) ? addrs : (addrs?.data ?? [])) };
    }

    case 'update_profile': {
      if (!confirmed) {
        return {
          status: 'awaiting_confirmation',
          message: 'Please confirm the changes with the patient before saving.',
        };
      }
      if (!updates || Object.keys(updates).length === 0) {
        return { error: 'No fields provided to update.' };
      }
      // SAFE MERGE: fetch current profile first, then merge updates.
      // PUT replaces the whole record — sending only changed fields wipes
      // unchanged fields (e.g. age → null → DB NOT NULL violation).
      const current = await apiGet(`/patients/${patientId}`, jwt);
      const merged  = { ...current, ...updates };
      // Ensure age is always a number (never null)
      if (merged.age == null && current.age != null) merged.age = current.age;
      if (merged.age == null) merged.age = 30; // absolute fallback
      return apiPut(`/patients/${patientId}`, merged, jwt);
    }

    case 'update_address': {
      if (!confirmed) {
        return {
          status: 'awaiting_confirmation',
          message: 'Please confirm the address changes with the patient before saving.',
        };
      }
      if (!addressId) return { error: 'addressId is required for update_address.' };
      if (!addressUpdates || Object.keys(addressUpdates).length === 0) {
        return { error: 'No address fields provided to update.' };
      }
      // PUT /patients/{patientId}/addresses/{addressId} — matches addressAPI.update()
      return apiPut(`/patients/${patientId}/addresses/${addressId}`, addressUpdates, jwt);
    }

    case 'add_address': {
      // 20 Aug 2026 — added because update_address cannot create.
      //
      // It requires an addressId, so when a patient gave a collection address
      // at a NEW pincode the assistant had no way to save it. The observed
      // consequence: a booking went ahead against pincode 333333 while the
      // patient's stored address stayed at 560078, so nothing persisted and the
      // order and the saved record disagreed. Worse, the only way to "save" it
      // with the tools available was to edit an existing address — overwriting
      // one the patient may still need.
      //
      // POST /patients/{patientId}/addresses — matches addressAPI.create() and
      // AddressDTO.CreateRequest (street, city, state, zipCode, country,
      // addressType, landmark, isDefault).
      if (!confirmed) {
        return {
          status: 'awaiting_confirmation',
          message: 'Please read the address back to the patient and confirm it before saving.',
        };
      }
      if (!addressUpdates || Object.keys(addressUpdates).length === 0) {
        return { error: 'Address fields are required for add_address.' };
      }
      // zipCode is the pincode a phlebotomist is dispatched to and the pincode
      // Thyrocare checks availability against, so an address without one is not
      // usable for home collection. Rejected here rather than saved incomplete.
      if (!addressUpdates.zipCode || !String(addressUpdates.zipCode).trim()) {
        return { error: 'zipCode (pincode) is required for a collection address.' };
      }
      if (!addressUpdates.street || !String(addressUpdates.street).trim()) {
        return {
          error: 'street is required — a pincode alone is not enough for a home visit. '
               + 'Ask the patient for the house/flat and street at that pincode.',
        };
      }
      return apiPost(`/patients/${patientId}/addresses`, {
        addressType: 'HOME',
        country:     'India',
        ...addressUpdates,
      }, jwt);
    }

    default:
      return { error: `Unknown action: ${action}` };
  }
}

// ─── Slot request builder ─────────────────────────────────────────────────────
// Thyrocare requires patients[] array in slot search.
// Claude sometimes omits it or sends wrong fields — this builds a valid shape.

// ── SKU ID normaliser ────────────────────────────────────────────────────────
// IMPORTANT: Thyrocare catalog IDs contain spaces and dots exactly as returned
// by the catalog API — e.g. "AAROGYAM 1.3", "AAROGYAM 1.2", "CARDIAC PROFILE - BASIC".
// These must be passed verbatim to orders and slot search.
//
// The ONLY normalisation needed is uppercase (Thyrocare is case-sensitive, uppercase only)
// and trim of leading/trailing whitespace. Internal spaces and dots MUST be preserved.
//
// DO NOT strip dots or internal spaces — "AAROGYAM13" is NOT a valid Thyrocare SKU ID;
// "AAROGYAM 1.3" is. Stripping dots/spaces was the root cause of "Invalid Test" errors.
function normaliseSKUId(id) {
  if (!id) return id;
  return String(id).trim().toUpperCase();
}

// Valid Thyrocare item types — used by both slot and order builders
const VALID_TYPES = ['OFFER', 'PSKU', 'SSKU', 'OSKU'];
function normaliseType(t) {
  if (!t) return 'SSKU';
  const upper = t.toUpperCase();
  // Only fix known-bad aliases — never overwrite a valid type from the catalog
  if (upper === 'PACKAGE' || upper === 'SKU') return 'SSKU';
  if (VALID_TYPES.includes(upper)) return upper;  // trust catalog value as-is
  // Unknown type — log and fall back to SSKU
  console.warn('[toolRouter] Unknown item type "' + t + '" — defaulting to SSKU');
  return 'SSKU';
}

function buildSafeSlotRequest(slotSearchRequest, toolInput) {
  const req = { ...slotSearchRequest };

  // Remove fields Thyrocare doesn't recognise
  delete req.preferredTime;

  // Thyrocare requires timeZone = 'IST' exactly.
  // Claude sends 'Asia/Kolkata' — override unconditionally.
  req.timeZone = 'IST';

  if (!req.patients || !Array.isArray(req.patients) || req.patients.length === 0) {
    const testCode = req.testCode || req.itemId || '';
    const testName = req.testName || testCode || 'Lab Test';
    const testType = normaliseType(req.testType);  // normalise, default SSKU

    delete req.testCode;
    delete req.testName;
    delete req.testType;
    delete req.itemId;

    req.patients = [{
      name:    toolInput.patientName || 'Patient',
      gender:  (toolInput.gender || 'MALE').toUpperCase(),
      age:     toolInput.age || 30,
      ageType: 'YEAR',
      items:   testCode ? [{ id: testCode, type: testType, name: testName }] : [],
    }];
  } else {
    // Normalise existing patients
    req.patients = req.patients.map(p => ({
      ...p,
      gender: p.gender ? p.gender.toUpperCase() : 'MALE',
      ageType: p.ageType ? p.ageType.toUpperCase() : 'YEAR',
      items: Array.isArray(p.items) ? p.items.map(item => ({
        // Whitelist only safe fields — do NOT spread full catalog item object
        // (leaks boolean flags like isFastingRequired which Thyrocare rejects).
        // normaliseSKUId preserves internal spaces/dots — "AAROGYAM 1.3" is correct.
        id:   normaliseSKUId(item.id),
        name: item.skuName || item.name,
        type: normaliseType(item.type),
      })) : p.items,
    }));
  }

  return req;
}

// ─── Complete order request builder ──────────────────────────────────────────
// Thyrocare requires many fields Claude may not populate.
// This fills in defaults and required structural fields from patient context.

// ─── Contact number normaliser ────────────────────────────────────────────────────────────────────────────
// Thyrocare requires '+CC-XXXXXXXXXX' e.g. '+91-9686836124'.
// Previu stores phones as '919686836124', '+919686836124', or '9686836124'.
function normaliseContactNumber(raw) {
  if (!raw) return raw;
  const digits = String(raw).replace(/\D/g, '');           // strip all non-digits
  const tenDigit = digits.length === 12 && digits.startsWith('91')
    ? digits.slice(2)                                       // 919686836124 → 9686836124
    : digits.length === 10
      ? digits                                              // 9686836124   → 9686836124
      : digits;
  return `+91-${tenDigit}`;                                // → +91-9686836124
}

// Patient-level contactNumber — Thyrocare expects plain 10-digit (no +91- prefix)
function normalisePatientContactNumber(raw) {
  if (!raw) return raw;
  const digits = String(raw).replace(/\D/g, '');           // strip all non-digits
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2); // 919686836124 → 9686836124
  if (digits.length === 10) return digits;                  // already 10-digit
  return digits;
}

function buildCompleteOrderRequest(orderRequest, toolInput) {
  const o = { ...orderRequest };

  // ── origin (required) ───────────────────────────────────────────────────
  if (!o.origin) {
    o.origin = {
      platform:   'All',
      appId:      'PREVIU',
      portalType: 'B2C',
      enteredBy:  'patient',
      source:     'B2C-CREATE-ORDER-API',
    };
  }

  // ── address (required) ──────────────────────────────────────────────────
  if (!o.address) {
    o.address = {
      addressLine1: 'Home Collection',
      city:         'Bangalore',
      state:        'Karnataka',
      country:      'India',
      pincode:      String(toolInput.slotSearchRequest?.pincode ?? '560001'),
    };
  }
  // Map from AddressDTO shape (street/zipCode) → Thyrocare shape (addressLine1/pincode)
  // AddressDTO.Response uses: street, city, state, zipCode, country, landmark
  // Thyrocare CreateOrderRequest.OrderAddress uses: addressLine1, city, state, country, pincode
  if (o.address.street && !o.address.addressLine1) {
    o.address.addressLine1 = o.address.street;
  }
  if (o.address.zipCode && !o.address.pincode) {
    o.address.pincode = String(o.address.zipCode);
  }

  // ── Pincode must match the pincode the SLOTS were searched at ───────────
  //
  // Confirmed bug (browser DevTools payloads, 20 Aug 2026):
  //   search_slots → pincode 333333        slots returned, patient picked 06:30
  //   create_order → address.pincode 560078  the patient's SAVED address
  // Thyrocare holds availability PER PINCODE, so it rejected the order with
  //   400 "Given slot is not available. Please select available slot"
  // — true and useless: the slot exists, just not at the pincode the order was
  // placed against.
  //
  // The two values come from independent sources and nothing reconciled them.
  // `slotSearchRequest.pincode` is whatever the conversation established;
  // `orderRequest.address` is typically copied from a get_addresses result. The
  // fallback above only ever applied when o.address was absent ENTIRELY, so an
  // address object carrying a stale zipCode always won.
  //
  // The searched pincode wins, for two reasons that agree:
  //   1. the slot being booked only exists at that pincode, so any other value
  //      cannot produce a valid order;
  //   2. it is the value the chat SHOWED the patient in the booking summary and
  //      that they confirmed — an order disagreeing with what was agreed is the
  //      bug, not a detail.
  //
  // The backend carries the same check (PanelSelectionGuardService) so a
  // mismatch is caught even if this reconciliation is bypassed. Both are needed:
  // this one produces a correct order, that one refuses an incorrect one.
  const searchedPincode = toolInput.slotSearchRequest?.pincode;
  if (searchedPincode) {
    const searched = String(searchedPincode).replace(/\D/g, '');
    const ordered  = String(o.address.pincode ?? '').replace(/\D/g, '');
    if (searched && ordered && searched !== ordered) {
      console.warn(
        `[toolRouter] create_order pincode ${ordered} disagrees with the pincode slots were ` +
        `searched at (${searched}). Using ${searched} — the chosen slot only exists there.`
      );
      o.address = { ...o.address, pincode: searched };
    } else if (searched && !ordered) {
      o.address = { ...o.address, pincode: searched };
    }
  }
  // Pass through optional address sub-fields from AddressDTO if present
  // Thyrocare sample includes these; omitting them is fine but including improves routing
  if (!o.address.addressLine2 && o.address.street) {
    o.address.addressLine2 = o.address.street; // street → addressLine2 as secondary line
  }
  if (!o.address.landmark && o.address.landmark !== '') {
    // only set if AddressDTO provided a landmark
    // leave undefined if not available — Thyrocare treats absent as empty
  }

  // addressLine1 must never be blank — Thyrocare rejects it.
  if (!o.address.addressLine1 || !o.address.addressLine1.trim()) {
    if (o.address.addressLine2 && o.address.addressLine2.trim()) {
      o.address.addressLine1 = o.address.addressLine2;
      o.address.addressLine2 = undefined;
    } else {
      o.address.addressLine1 = 'Home Collection';
    }
  }
  // Ensure required address fields have defaults
  if (!o.address.city)    o.address.city    = 'Bangalore';
  if (!o.address.state)   o.address.state   = 'Karnataka';
  if (!o.address.country) o.address.country = 'India';
  if (o.address.pincode)  o.address.pincode = String(o.address.pincode);
  // Thyrocare sample shows city/state in UPPERCASE
  if (o.address.city)  o.address.city  = o.address.city.toUpperCase();
  if (o.address.state) o.address.state = o.address.state.toUpperCase();

  // ── appointment (required) ──────────────────────────────────────────────
  if (!o.appointment) {
    o.appointment = {
      date:      toolInput.slotSearchRequest?.appointmentDate ?? new Date().toISOString().slice(0, 10),
      startTime: '09:00',
      timeZone:  'IST',
    };
  }
  // Always ensure timeZone is set — the original error was timeZone=undefined, not IST.
  // Thyrocare's own sample JSON uses 'IST'. Only fix if missing/null.
  if (!o.appointment.timeZone) {
    o.appointment.timeZone = 'IST';
  }

  // ── price (required) ────────────────────────────────────────────────────
  if (!o.price) {
    o.price = {
      discounts: [{ type: 'HOME_VISIT_DISCOUNT', amount: '' }],
      incentivePasson: { type: 'FLAT', value: '0' },
    };
  }

  // ── paymentDetails (required) ───────────────────────────────────────────
  if (!o.paymentDetails) {
    o.paymentDetails = { payType: 'POSTPAID' };
  }

  // ── attributes (required) ───────────────────────────────────────────────
  if (!o.attributes) {
    o.attributes = {
      remarks:                  '',
      phleboNotes:              '',
      campId:                   null,
      isReportHardCopyRequired: false,
      refOrderNo:               `PRV-${(toolInput.patientId || '').slice(-8)}-${String(Date.now()).slice(-6)}`, // max 25 chars
      collectionType:           'HOME_COLLECTION',
      alertMessage:             [''],
    };
  }

  // ── always overwrite refOrderNo — Claude may send a too-long value ───────────────
  if (!o.attributes) o.attributes = {};
  o.attributes.refOrderNo = `PRV-${(toolInput.patientId || '').slice(-8)}-${String(Date.now()).slice(-6)}`;
  o.attributes.isReportHardCopyRequired = false; // Thyrocare requires explicit false — must not be omitted

  // ── always set isPdpcOrder — Thyrocare requires explicit false ────────────────
  if (!o.orderOptions) o.orderOptions = {};
  o.orderOptions.isPdpcOrder = false;

  // ── referredBy (optional but expected) ─────────────────────────────────
  if (!o.referredBy) {
    o.referredBy = { doctorId: '', doctorName: '' };
  }

  // ── config ───────────────────────────────────────────────────────────────
  if (!o.config) {
    o.config = {
      communication: {
        shareReport:  true,
        shareReceipt: true,
        shareModes:   { whatsapp: true, email: true },
      },
    };
  }

  // ── orderOptions ─────────────────────────────────────────────────────────
  if (!o.orderOptions) {
    o.orderOptions = { isPdpcOrder: false };
  }

  // ── patients — ensure age is string, items have origin ──────────────────
  if (Array.isArray(o.patients)) {
    o.patients = o.patients.map(p => ({
      ...p,
      age:     String(p.age ?? '30'),         // Thyrocare requires string
      ageType: (p.ageType ?? 'YEAR').toUpperCase(),
      gender:  (p.gender  ?? 'MALE').toUpperCase(),
      // Thyrocare requires a non-empty email inside each patient.
      // Hoist from order-level if missing from patient. Never send empty string.
      email: (p.email || o.email || '').trim().replace(/,.*$/, '').toLowerCase() || undefined,
      contactNumber: normalisePatientContactNumber(p.contactNumber ?? o.contactNumber), // plain 10-digit at patient level
      attributes: p.attributes ?? {
        ulcUniqueCode:      '',
        patientAddress:     'Home Collection',
        externalPatientId:  '',
      },
      documents: p.documents ?? [{
        attributes:    '{}',
        documentPath:  'document-path-if-any',
        documentType:  'document-type-if-any',
      }],
      items: Array.isArray(p.items) ? p.items.map(item => ({
        // Whitelist only fields Thyrocare accepts — spreading the full item
        // leaks catalog booleans (isHomeCollectible: null, isFastingRequired: true, etc.)
        // which cause "Invalid Test : false" / "Invalid Test : null" rejections.
        //
        // ── SKU ID: pass verbatim from catalog ───────────────────────────
        // Thyrocare catalog IDs contain spaces and dots: "AAROGYAM 1.3" not "AAROGYAM13".
        // normaliseSKUId now only trims + uppercases — internal spaces/dots preserved.
        id:     normaliseSKUId(item.id),
        // ── SKU name: must match catalog exactly ─────────────────────────
        // Use skuName from get_panel_pricing if available (most reliable source).
        // Fall back to item.name as passed by Claude.
        // Catalog name for "AAROGYAM 1.3" is "AAROGYAM 1.3" (uppercase, space+dot preserved).
        name:   item.skuName || item.name,
        type:   normaliseType(item.type),
        origin: item.origin ?? { enteredBy: 'patient', platform: 'ALL' },
      })) : p.items,
    }));
  }

  // ── normalise top-level contactNumber ───────────────────────────────────
  // Thyrocare requires '+CC-XXXXXXXXXX' format at order level.
  if (o.contactNumber) o.contactNumber = normaliseContactNumber(o.contactNumber);

  // ── normalise email — trim whitespace + lowercase ─────────────────────
  // Patient sometimes provides "email@x.com , 9876543210" as one string.
  // Claude may pass email with trailing spaces or commas.
  if (o.email) {
    o.email = String(o.email).trim().replace(/,.*$/, '').trim().toLowerCase();
  }
  // Thyrocare requires a valid email at order level — never send empty string
  if (!o.email || !o.email.includes('@')) {
    // Remove the field entirely rather than send '' — Thyrocare 500s on empty email
    delete o.email;
  }

  // ── top-level contactNumber fallback ────────────────────────────────────
  // If Claude put contactNumber only inside patients[] but not at top level,
  // hoist it up — Thyrocare requires it at both levels.
  if (!o.contactNumber && Array.isArray(o.patients) && o.patients[0]?.contactNumber) {
    o.contactNumber = normaliseContactNumber(o.patients[0].contactNumber);
  }

  // ── sanitise any numeric price fields to strings ─────────────────────────
  // ── strip all boolean false values — Thyrocare rejects them as "Invalid Test : false"
  return stripFalseValues(sanitiseOrderRequest(o));
}

// ─── Boolean false stripper ──────────────────────────────────────────────────
// Thyrocare rejects ANY `false` value anywhere in the order payload with
// "Invalid Test : false". Strip all false booleans recursively; omitting
// them is safe as Thyrocare treats missing optional flags as false by default.
// Fields Thyrocare requires as explicit false — must never be stripped
const REQUIRED_FALSE_FIELDS = new Set(['isReportHardCopyRequired', 'isPdpcOrder']);

function stripFalseValues(obj) {
  if (Array.isArray(obj)) {
    return obj.map(stripFalseValues);
  }
  if (obj && typeof obj === 'object') {
    const result = {};
    for (const [key, val] of Object.entries(obj)) {
      if (val === false && !REQUIRED_FALSE_FIELDS.has(key)) continue; // drop optional false booleans
      result[key] = stripFalseValues(val);
    }
    return result;
  }
  return obj;
}

// ─── Order request sanitiser ─────────────────────────────────────────────────
// CreateOrderRequest$OrderPrice expects all price/amount fields as Strings.
// Claude occasionally sends them as Numbers — this coerces them safely.

function sanitiseOrderRequest(order) {
  if (!order || typeof order !== 'object') return order;

  const coerce = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const result = Array.isArray(obj) ? [] : {};
    for (const [key, val] of Object.entries(obj)) {
      // Price/amount/rate fields must be strings
      const priceKeys = ['amount', 'price', 'sellingPrice', 'listingPrice',
                         'rate', 'totalAmount', 'discount', 'tax'];
      if (priceKeys.includes(key) && typeof val === 'number') {
        result[key] = String(val);
      } else if (val && typeof val === 'object') {
        result[key] = coerce(val);
      } else {
        result[key] = val;
      }
    }
    return result;
  };

  return coerce(order);
}

// ─── download_triage_report ──────────────────────────────────────────────────
// New 15 Jul 2026, UPDATED 15 Jul (later) for UX — no longer auto-opens the
// print window as a side effect of the tool call. Returns a "card" result
// instead; the chat UI renders it as a clickable report card
// (TriageReportCard.jsx) and the ACTUAL print/download only happens when the
// patient clicks it. The underlying download mechanism
// (patientReportDownload.js's downloadPatientReport) is unchanged — this is a
// UX change in WHEN it fires, not a capability change.
//
// triage-history is sorted DESC (confirmed against the actual backend query,
// TriageResultRepository.findLatestPerHealthRecordWithDrawDate — "ORDER BY
// hr.drawDate DESC NULLS LAST, t.createdAt DESC") — [0] is genuinely the
// latest report, not assumed.
async function handleDownloadTriageReport(input, jwt) {
  const { patientId } = input;
  if (!patientId) {
    return { error: 'patientId is required.' };
  }

  const [patient, triageHistory] = await Promise.all([
    apiGet(`/patients/${patientId}`, jwt),
    apiGet(`/patients/${patientId}/triage-history`, jwt),
  ]);

  if (!Array.isArray(triageHistory) || triageHistory.length === 0) {
    return { error: 'No triage report found for this patient yet — they need at least one completed lab report triage first.' };
  }

  const latest = triageHistory[0];
  const reportId = `PRV-${String(patient?.id || '').slice(0, 8).toUpperCase()}-` +
    `${new Date(latest.createdAt || Date.now()).toISOString().slice(0, 10).replace(/-/g, '')}`;

  // Full patient/triageResult/triageHistory travel with the result so
  // TriageReportCard can call downloadPatientReport() directly on click,
  // without a second fetch. Claude itself only ever sees the trimmed
  // summary built in chatService.js's trimToolResult() — never this full
  // object (see that function's new 'download_triage_report' case).
  return {
    type: 'triage_report_card',
    patient,
    triageResult: latest,
    triageHistory,
    reportId,
    drawDate: latest.drawDate,
    riskLevel: latest.consensusRiskLevel,
    riskScore: latest.consensusRiskScore,
  };
}

// ─── show_marker_trend ───────────────────────────────────────────────────────
// Fetches historical values of a specific marker from the patient's triage
// history. Returns a structured array for the frontend MarkerTrendWidget.
//
// ⚠ Constitution §III: returns raw numerical data only — no interpretation.
// ⚠ PENDING Dr. Ajay Kumar sign-off before go-live.
// Ref: CONTEXT_frontend.md — Pre-go-live sign-offs.

async function handleShowMarkerTrend(input, jwt) {
  const { patientId, markerName, markerNames } = input;

  if (!patientId || (!markerName && !markerNames)) {
    return { error: 'patientId and markerName (or markerNames array) are required.' };
  }

  // Normalise: accept both single string and array
  const requestedMarkers = markerNames
    ? (Array.isArray(markerNames) ? markerNames : [markerNames])
    : [markerName];

  // Fetch triage history ONCE regardless of how many markers requested
  const triageHistory = await apiGet(`/patients/${patientId}/triage-history`, jwt);

  if (!Array.isArray(triageHistory) || triageHistory.length === 0) {
    return { error: 'No triage history found for this patient.' };
  }

  // Parse all draws once
  const parsedDraws = triageHistory.map(result => {
    if (!result.claudeRawResponse) return null;
    try {
      let raw = result.claudeRawResponse;
      let parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return { drawDate: result.drawDate || result.createdAt?.split('T')[0], parsed };
    } catch { return null; }
  }).filter(Boolean);

  // Build data for each requested marker
  const results = {};
  const availableMarkers = [...new Set(parsedDraws.flatMap(d =>
    (d.parsed?.markers || d.parsed?.biomarkers || d.parsed?.lab_results || [])
      .map(m => m.name).filter(Boolean)
  ))];

  for (const name of requestedMarkers) {
    const nameLower = name.toLowerCase();
    const dataPoints = [];

    for (const draw of parsedDraws) {
      const markers = draw.parsed?.markers || draw.parsed?.biomarkers || draw.parsed?.lab_results || [];
      const match = markers.find(m => {
        const mName = (m.name || m.marker_name || m.marker || '').toLowerCase();
        return mName.includes(nameLower) || nameLower.includes(mName.split(' ')[0]);
      });
      if (!match) continue;
      const rawVal = match.value ?? match.result ?? match.val ?? match.reading;
      const value  = parseFloat(String(rawVal).replace(/[^0-9.]/g, ''));
      if (isNaN(value)) continue;
      dataPoints.push({
        drawDate: draw.drawDate,
        value:    Math.round(value * 100) / 100,
        unit:     match.unit || match.units || '',
        status:   (match.status || match.flag || match.interpretation || '').toLowerCase(),
      });
    }
    results[name] = { dataPoints, unit: dataPoints[0]?.unit || '' };
  }

  // If single marker requested, return original shape for backward compat
  if (requestedMarkers.length === 1) {
    const name = requestedMarkers[0];
    const { dataPoints, unit } = results[name];
    if (dataPoints.length === 0) {
      return {
        type: 'marker_trend', markerName: name, unit: '', dataPoints: [], drawCount: 0,
        availableMarkers: availableMarkers.slice(0, 10),
        note: `No data found for "${name}". Available: ${availableMarkers.slice(0, 8).join(', ')}.`,
      };
    }
    return { type: 'marker_trend', markerName: name, unit, dataPoints, drawCount: dataPoints.length };
  }

  // Multiple markers — return combined shape
  console.log('[toolRouter] show_marker_trend availableMarkers:', availableMarkers);
  console.log('[toolRouter] results summary:', Object.fromEntries(
    Object.entries(results).map(([k, v]) => [k, v.dataPoints.length])
  ));
  return {
    type:        'marker_trend_multi',
    markerNames: requestedMarkers,
    results,
    drawCount:   triageHistory.length,
    availableMarkers: availableMarkers.slice(0, 10),
  };
}

// ─── get_panel_pricing ───────────────────────────────────────────────────────
// Calls POST /api/labtest/panel-pricing — the server:
//   1. Resolves Previu canonical panel definitions (Constitution §1.3)
//   2. Fetches matching lab partner SKUs for pricing data
//   3. Runs coverage comparison (Previu tests vs SKU tests)
//   4. Persists reasoning to panel_pricing_queries table
//   5. Returns Previu-branded breakdown — NO lab partner name exposed
//
// Constitution §1.3: Panel Architecture — Locked May 2026.
// ⚠ PENDING Dr. Ajay sign-off on panel pricing intelligence feature.

async function handleGetPanelPricing(input, jwt) {
  const { patientId, panels, skuQueries } = input;

  if (!panels || panels.length === 0) {
    return { error: 'panels array is required (e.g. ["T2", "Cardiac"])' };
  }

  return apiPost('/labtest/panel-pricing', {
    patientId,
    panels,
    skuQueries: skuQueries || [],
  }, jwt);
}

// ─── submit_symptom_answers ────────────────────────────────────────────────
// 8 Jul 2026: records onboarding symptom questionnaire answers (Constitution
// v9.7 §VIII). Maps directly onto POST /api/patients/{id}/symptoms —
// SymptomQuestionnaireController on the backend. No confirmation step
// (unlike handleUpdateProfile) since recording a conversational answer is
// lower-stakes than mutating existing profile data; the backend itself
// handles the CRITICAL flag logic for Q19/Q20 on save.
async function handleSubmitSymptomAnswers(input, jwt) {
  const { patientId, episode, answers } = input;
  if (!patientId) return { error: 'patientId is required.' };
  if (!answers || answers.length === 0) return { error: 'At least one answer is required.' };

  return apiPost(`/patients/${patientId}/symptoms`, {
    episode: episode || 'ONBOARDING',
    source: 'CHAT_ONBOARDING',
    answers,
  }, jwt);
}

// ─── Public dispatcher ────────────────────────────────────────────────────────

export async function executeToolCall(toolName, input, jwt) {
  // 15 Jul 2026: unconditional log — confirms the dispatcher itself was
  // reached with this tool name/input, independent of whether execution
  // then succeeds or fails. Added while diagnosing "download latest triage
  // report" — see chatService.js's turn-result log for the earlier-stage
  // check (did Claude even emit a tool_use block at all).
  console.log('[toolRouter] executeToolCall:', toolName, input);
  switch (toolName) {
    case 'book_appointment':         return handleBookAppointment(input, jwt);
    case 'book_navigator_appointment': return handleBookNavigatorAppointment(input, jwt);
    case 'add_navigator_note':       return handleAddNavigatorNote(input, jwt);
    case 'order_lab_test':           return handleOrderLabTest(input, jwt);
    case 'update_profile':           return handleUpdateProfile(input, jwt);
    case 'show_marker_trend':        return handleShowMarkerTrend(input, jwt);
    case 'download_triage_report':   return handleDownloadTriageReport(input, jwt);
    case 'get_panel_pricing':        return handleGetPanelPricing(input, jwt);
    case 'submit_symptom_answers':   return handleSubmitSymptomAnswers(input, jwt);
    default: return { error: `Unknown tool: ${toolName}` };
  }
}
